import { useState, useRef } from "react";
import type { Product } from "@/types/product";
import { categoryTagStyles } from "@/mocks/products";

interface ProductCardProps {
  product: Product;
  onEdit: (product: Product) => void;
  onDelete: (id: string) => void;
  isAdmin: boolean;
  currentUserId: string | null;
}

export default function ProductCard({ product, onEdit, onDelete, isAdmin, currentUserId }: ProductCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isOwner = currentUserId && product.createdBy === currentUserId;
  const canEdit = isAdmin || isOwner;

  const isOutOfStock = product.stock === 0;

  const formatPrice = (price: number) => `NT$ ${price.toLocaleString("zh-TW")}`;

  return (
    <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden group hover:border-primary-300 transition-colors">
      {/* Image Area */}
      <div className="relative aspect-[3/4] bg-white overflow-hidden">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-contain object-center p-2 group-hover:scale-[1.02] transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className="ri-image-line text-foreground-300 text-4xl" />
          </div>
        )}
        {/* Status Overlay */}
        {product.status === "inactive" && (
          <div className="absolute inset-0 bg-foreground-900/50 flex items-center justify-center">
            <span className="text-background-50 text-sm font-semibold px-3 py-1 bg-foreground-800/80 rounded-full">
              已下架
            </span>
          </div>
        )}
        {/* Stock Alert - only show sold out */}
        {isOutOfStock && product.status === "active" && (
          <div className="absolute top-2 right-2 bg-accent-500 text-background-50 text-[10px] font-semibold px-2 py-1 rounded-full">
            已售罄
          </div>
        )}
        {/* Action Menu - only shown if user can edit */}
        {canEdit && (
          <div className="absolute top-2 left-2">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="w-8 h-8 rounded-lg bg-background-50/90 backdrop-blur-sm flex items-center justify-center text-foreground-600 hover:text-foreground-900 cursor-pointer transition-colors"
            >
              <i className="ri-more-2-fill" />
            </button>
            {showMenu && (
              <div
                ref={menuRef}
                className="absolute top-10 left-0 w-32 bg-background-50 border border-background-200 rounded-lg shadow-lg overflow-hidden z-20"
              >
                <button
                  onClick={() => {
                    onEdit(product);
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-3 py-2 text-xs text-foreground-700 hover:bg-background-100 cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-edit-line text-foreground-400" />
                  編輯商品
                </button>
                <button
                  onClick={() => {
                    onDelete(product.id);
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-3 py-2 text-xs text-accent-600 hover:bg-accent-50 cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-delete-bin-line text-accent-400" />
                  刪除商品
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info Area */}
      <div className="p-3 space-y-2">
        {/* Category Tag */}
        <div className="flex items-center gap-2">
          <span
            className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${categoryTagStyles[product.category] || "bg-background-100 text-foreground-600"}`}
          >
            {product.category}
          </span>
          <span className="text-[10px] text-foreground-400">{product.vintage}</span>
        </div>

        {/* Name */}
        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1">
          {product.brand} {product.name}
        </h3>

        {/* Origin & Grape */}
        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
          {product.country} · {product.region} · {product.grape}
        </p>

        {/* Price & Stock */}
        <div className="flex items-center justify-between pt-1">
          <span className="text-sm font-bold text-foreground-900">
            {formatPrice(product.sellingPrice)}
          </span>
          <span className={`text-xs font-medium ${isOutOfStock ? "text-accent-500" : "text-foreground-500"}`}>
            庫存 {product.stock} 瓶
          </span>
        </div>
      </div>
    </div>
  );
}